/* https://brandplatform.smartphoto.com/general/molecules/file-upload/ */
import { trackContactPageEvent } from "../../common/events";
import { expressEventListener, expressQuerySelector, expressQuerySelectorAll } from "../../common/html";
import { compositeDisposable } from "../../utils/disposable";
import { postRequest } from "../../utils/fetch";
import { Guid } from "../../utils/guid";

export interface IGetSignedUrl {
	sessionId: string;
	url: string;
}

export interface IFileUploadDependencies {
	getPreventedFileTypes: () => Promise<string>;
	getUploadUrl: (sessionId: string, filename: string) => Promise<IGetSignedUrl>;
	onUploadComplete: () => void;
	onUploadFailed: () => void;
	onUploadLoad: () => void;
	onUpload: () => void;
	onFilesSelected: () => void;
	onFileDeleted: () => void;
	showInformation: (title: string, label: string) => void;
}

export default function createFileUpload(containerEl: HTMLElement, deps: IFileUploadDependencies) {
	const infoButtons = expressQuerySelectorAll<HTMLElement>(containerEl, ".technical-info-button");
	const files = expressQuerySelector<HTMLElement>(containerEl, ".technical-contact-form-files", true);
	const fileInput = expressQuerySelector<HTMLInputElement>(files, ".technical-upload-input", true);
	const fileTemplate = expressQuerySelector<HTMLElement>(files, ".technical-contact-from-file-template", true);
	const useExtendedLogging = files.dataset.logging === "true";

	let filesUsed: string[] = [];
	let filesUploading: string[] = [];
	let selectedFilesUploading = false;
	const filesAlreadyUploaded: string[] = [];
	let sessionId;
	const {
		getPreventedFileTypes,
		onFilesSelected,
		getUploadUrl,
		onUploadComplete,
		onUploadFailed,
		onUploadLoad,
		onUpload,
		showInformation,
		onFileDeleted,
	} = deps;

	const startUpload = (e: Event) => {
		trackContactPageEvent("UploadFiles");

		// For each file, we need to start an upload. Only first 5 files are taken into account, we ignore more ...
		e.preventDefault();

		const fileInput = e.currentTarget as HTMLInputElement;

		const allFiles = fileInput.files;
		if (!allFiles) return; // Nothing selected

		if (!sessionId) sessionId = Guid.newGuid();

		selectedFilesUploading = filesUsed.length + filesUploading.length < 5;
		onFilesSelected();

		for (let i = 0; i < allFiles.length; i++) {
			const file = allFiles[i];

			if (filesUsed.indexOf(file.name) !== -1) continue;

			// Display the file in the list of files
			const newFile = fileTemplate.cloneNode(true) as HTMLElement;
			newFile.classList.remove("u-hide", "technical-contact-from-file-template");
			newFile.classList.add("technical-contact-from-file");
			newFile.dataset.filename = file.name;
			const filename = expressQuerySelector<HTMLSelectElement>(newFile, ".technical-loadingbar-text", true);
			filename.innerHTML = file.name;
			files.appendChild(newFile);

			// if more then max for upload show and start timer that user can not upload more then X files
			if (i >= 5 - (filesUsed.length + filesUploading.length)) {
				setProcessbarState(newFile, true, fileInput.dataset.fileuploadlimiterror || "");
				selectedFilesUploading = filesUploading.length > 0;
				continue;
			}

			getPreventedFileTypes().then((res) => {
				const fileType = file.name.split(".").pop() || "";
				if (res.toLowerCase().indexOf(fileType.toLowerCase()) === -1) {
					if (filesUsed.indexOf(file.name) === -1 || filesUploading.indexOf(file.name) === -1) {
						getUploadUrl(sessionId, file.name).then((res) => {
							if (file.size === 0 || file.size > 1024 * 1024 * 20) {
								setProcessbarState(newFile, true, fileInput.dataset.filelimiterror || "");
							} else {
								filesUploading.push(file.name);

								// onUpload valid files
								onUpload();

								const progress = expressQuerySelector<HTMLSelectElement>(
									newFile,
									".technical-loadingbar-progress",
									true,
								);
								const visualprogress = expressQuerySelector<HTMLSelectElement>(
									newFile,
									".technical-loadingbar-visual-progress",
									true,
								);

								try {
									// get data from form
									const formdata = new FormData();
									formdata.append("file", file, file.name);
									const ajax = new XMLHttpRequest();
									ajax.open("POST", res.url);

									// showprogress upload
									ajax.upload.addEventListener("progress", (evt: ProgressEvent) => {
										if (evt.lengthComputable) {
											const percentComplete = (evt.loaded / evt.total) * 100;
											progress.innerHTML = Math.round(percentComplete) + "%";
											visualprogress.style.width = Math.round(percentComplete) + "%";
										}
									});

									// load progress
									ajax.upload.addEventListener("load", (evt: ProgressEvent) => {
										filesAlreadyUploaded.push(file.name);
										onUploadLoad();
										// clean fileinput
										fileInput.value = "";
									});

									// state change
									ajax.onreadystatechange = () => {
										filesUploading = filesUploading.filter((f) => f !== file.name);
										selectedFilesUploading = filesUploading.length > 0;

										if (
											ajax.status &&
											((ajax.status >= 200 && ajax.status < 300) || filesAlreadyUploaded.indexOf(file.name) !== -1)
										) {
											progress.innerHTML = "100%";
											visualprogress.style.width = "100%";

											if (filesUsed.indexOf(file.name) === -1) {
												filesUsed.push(file.name);
												setProcessbarState(
													newFile,
													false,
													"file " + file.name + " was uploaded successfully",
													ajax.status,
												);
											}

											onUploadComplete();
										} else {
											onUploadFailed();
											setProcessbarState(
												newFile,
												true,
												(fileInput.dataset.fileuploaderror || "Upload to contact form error.Filename: {0}").replace(
													"{0}",
													file.name,
												),
												ajax.status,
											);
											selectedFilesUploading = filesUploading.length > 0;
										}
										// clean fileinput
										fileInput.value = "";
									};
									ajax.send(formdata);
								} catch (err) {
									setProcessbarState(
										newFile,
										true,
										(fileInput.dataset.fileuploaderror || "Upload to contact form error. Filename: {0}").replace(
											"{0}",
											file.name,
										),
										undefined,
										err as string | undefined,
									);
									selectedFilesUploading = filesUploading.length > 0;
								}
							}
						});
					}
				} else {
					setProcessbarState(newFile, true, fileInput.dataset.fileextensionnotvaliderror || "");
					selectedFilesUploading = filesUploading.length > 0;
				}
			});
		}
	};

	const isUploadComplete = () => {
		return filesUploading.length <= 0 && !selectedFilesUploading;
	};

	const filesClicked = (e: MouseEvent) => {
		const target = e.target as HTMLElement;

		if (
			target.classList.contains("technical-progressbar-delete") &&
			target.parentElement &&
			target.parentElement.parentElement
		) {
			// Clicked the delete of a file
			deleteprogressbar(target.parentElement.parentElement);
		}
	};

	const log = (message: string, error: boolean) => {
		postRequest(
			"/apicore/Logapi/LogInformation",
			{ InformationText: message, Error: error },
			{ headers: { "Content-type": "application/json; charset=utf-8" } },
		);
	};

	const deleteprogressbar = (el: HTMLElement) => {
		if (el) {
			const filename = expressQuerySelector(el, ".technical-loadingbar-text", true).innerHTML;
			if (filename) filesUsed = filesUsed.filter((f) => f !== filename);

			selectedFilesUploading = filesUploading.length > 0;

			el.remove();
			onFileDeleted();
		}
	};

	const deleteprogressbartimer = (el: HTMLElement) => {
		const errormessageCountDown = expressQuerySelector(el, ".m-progress-bar__error__timer", true);
		let timerseconds = parseInt(
			errormessageCountDown.getAttribute("errortimerseconds")
				? errormessageCountDown.getAttribute("errortimerseconds")!
				: "",
			10,
		);

		if (timerseconds) {
			const intervalId = setInterval(() => {
				// if seconds 0
				if (timerseconds <= 1) {
					clearInterval(intervalId);
					deleteprogressbar(el);
				}

				// countdown seconds
				timerseconds--;
				errormessageCountDown.innerHTML = timerseconds.toString() + "s";
			}, 1000);
		}
	};

	const updateBarPercentage = (percentage: number) => {
		const progress = expressQuerySelector<HTMLSelectElement>(containerEl, ".technical-loadingbar-progress", true);
		const visualprogress = expressQuerySelector<HTMLSelectElement>(
			containerEl,
			".technical-loadingbar-visual-progress",
			true,
		);

		progress.innerHTML = Math.round(percentage) + "%";
		visualprogress.style.width = Math.round(percentage) + "%";
	};

	const getFileUrls = () => {
		return filesUsed.map((f) => sessionId + "/" + f).join("||");
	};

	const clickShowInformation = (e: MouseEvent) => {
		const button = e.currentTarget as HTMLElement;

		if (button.dataset.title && button.dataset.label) showInformation(button.dataset.title, button.dataset.label);
	};

	const eventDisposables = compositeDisposable([
		expressEventListener(fileInput, "change", startUpload),
		expressEventListener(files, "click", filesClicked),
	]);

	const setProcessbarState = (
		el: HTMLElement,
		error: boolean,
		message: string,
		status?: number,
		errorMessageForLogging?: string,
	) => {
		// if progressbar
		if (!el.classList.contains("m-progress-bar")) return;

		// set loadingbar error
		const loadingbar = expressQuerySelector(el, ".a-loading-bar", true);
		loadingbar.classList.add(error ? "a-loading-bar--error" : "a-loading-bar--success");
		loadingbar.classList.remove(!error ? "a-loading-bar--error" : "a-loading-bar--success");

		// set progressbar error
		el.classList.remove("m-progress-bar--loading");
		el.classList.remove(error ? "m-progress-bar--success" : "m-progress-bar--error");
		el.classList.add(!error ? "m-progress-bar--success" : "m-progress-bar--error");

		// set error message progressbar
		const errorspan = expressQuerySelector(el, ".m-progress-bar__error", false);
		if (errorspan && error) {
			const errormessagespan = expressQuerySelector(el, ".m-progress-bar__error__message", true);
			errormessagespan.innerHTML = message ? message : "";

			// add timer
			deleteprogressbartimer(el);
		}

		// log error for logviewer
		if (useExtendedLogging) {
			const filename = expressQuerySelector<HTMLElement>(loadingbar, ".technical-loadingbar-text", false);
			const logMessage =
				message +
				(errorMessageForLogging ? errorMessageForLogging : "") +
				(status ? " with status " + status : "") +
				(error
					? filename
						? "\n happend on file " + filename.innerHTML + "\n this happend on session id " + sessionId
						: "\n this happend on session id " + sessionId
					: " file is now in bucket folder: " + sessionId);
			log(logMessage, error);
		}
	};

	// Enable the mini info buttons
	infoButtons.forEach((b) => {
		eventDisposables &&
			eventDisposables.add &&
			eventDisposables.add(expressEventListener(b, "click", clickShowInformation));
	});

	return {
		dispose: () => {
			eventDisposables.dispose();
		},
		SetProgressBarState: setProcessbarState,
		UpdateProgressBarPercentage: updateBarPercentage,
		IsUploadComplete: isUploadComplete,
		GetFileUrls: getFileUrls,
	};
}
